import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { X, FileText, ExternalLink, Plus, GripVertical } from 'lucide-react';
import UnifiedFileUpload from '@/components/ui/UnifiedFileUpload';

interface IntakeForm {
  id: string;
  form_type: 'pdf' | 'external_link';
  title: string;
  description: string | null;
  file_url: string | null;
  external_url: string | null;
  display_order: number;
  is_active: boolean;
  created_at: string;
}

interface IntakeFormsManagerProps {
  practitionerId: string;
  onUpdate?: () => void;
}

export default function IntakeFormsManager({ practitionerId, onUpdate }: IntakeFormsManagerProps) {
  const [forms, setForms] = useState<IntakeForm[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation('dashboard');

  // New form state
  const [newFormType, setNewFormType] = useState<'pdf' | 'external_link'>('pdf');
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newExternalUrl, setNewExternalUrl] = useState('');
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  useEffect(() => {
    fetchForms();
  }, [practitionerId]);

  const fetchForms = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('practitioner_intake_forms')
        .select('*')
        .eq('practitioner_id', practitionerId)
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      setForms((data as IntakeForm[]) || []);
    } catch (error) {
      console.error('Error fetching intake forms:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load intake forms.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const resetNewForm = () => {
    setNewFormType('pdf');
    setNewTitle('');
    setNewDescription('');
    setNewExternalUrl('');
    setPendingFile(null);
    setIsAdding(false);
  };

  const handleSaveForm = async () => {
    if (!newTitle.trim()) {
      toast({ variant: 'destructive', title: 'Error', description: 'Title is required.' });
      return;
    }

    if (newFormType === 'external_link' && !newExternalUrl.trim()) {
      toast({ variant: 'destructive', title: 'Error', description: 'External URL is required.' });
      return;
    }

    if (newFormType === 'pdf' && !pendingFile) {
      toast({ variant: 'destructive', title: 'Error', description: 'Please select a PDF file.' });
      return;
    }

    setIsSaving(true);

    try {
      let fileUrl: string | null = null;

      if (newFormType === 'pdf' && pendingFile) {
        const fileName = `${practitionerId}/${Date.now()}-${pendingFile.name}`;
        const { error: uploadError } = await supabase.storage
          .from('intake-forms')
          .upload(fileName, pendingFile, { contentType: 'application/pdf' });

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from('intake-forms')
          .getPublicUrl(fileName);
        
        // Since bucket is private, we'll store the path and generate signed URLs when needed
        fileUrl = fileName;
      }

      const { error } = await supabase
        .from('practitioner_intake_forms')
        .insert({
          practitioner_id: practitionerId,
          form_type: newFormType,
          title: newTitle.trim(),
          description: newDescription.trim() || null,
          file_url: fileUrl,
          external_url: newFormType === 'external_link' ? newExternalUrl.trim() : null,
          display_order: forms.length,
        } as any);

      if (error) throw error;

      await fetchForms();
      onUpdate?.();
      resetNewForm();

      toast({ title: 'Success', description: 'Intake form added successfully.' });
    } catch (error: any) {
      console.error('Error saving intake form:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message?.includes('Maximum 5') 
          ? 'Maximum 5 active intake forms allowed.' 
          : 'Failed to save intake form.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteForm = async (form: IntakeForm) => {
    try {
      // Soft delete
      const { error } = await supabase
        .from('practitioner_intake_forms')
        .update({ is_active: false } as any)
        .eq('id', form.id);

      if (error) throw error;

      // Also delete file from storage if it's a PDF
      if (form.form_type === 'pdf' && form.file_url) {
        await supabase.storage.from('intake-forms').remove([form.file_url]);
      }

      await fetchForms();
      onUpdate?.();

      toast({ title: 'Success', description: 'Intake form removed.' });
    } catch (error) {
      console.error('Error deleting intake form:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to remove intake form.',
      });
    }
  };

  if (isLoading) {
    return <div className="text-center py-4">Loading intake forms...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Intake Forms & Consent Documents
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Upload up to 5 intake forms or consent documents. These will be automatically sent to clients when you confirm their booking. ({forms.length}/5)
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Existing Forms List */}
        {forms.length > 0 && (
          <div className="space-y-3">
            {forms.map((form, index) => (
              <div
                key={form.id}
                className="flex items-center gap-3 p-3 border border-border rounded-lg bg-muted/30"
              >
                <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex items-center gap-2 shrink-0">
                  {form.form_type === 'pdf' ? (
                    <FileText className="h-4 w-4 text-destructive" />
                  ) : (
                    <ExternalLink className="h-4 w-4 text-primary" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{form.title}</p>
                  {form.description && (
                    <p className="text-xs text-muted-foreground truncate">{form.description}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {form.form_type === 'pdf' ? 'PDF Document' : form.external_url}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 shrink-0"
                  onClick={() => handleDeleteForm(form)}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {forms.length === 0 && !isAdding && (
          <div className="text-center py-6 text-muted-foreground">
            <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No intake forms added yet.</p>
            <p className="text-sm">Add forms that clients should complete before their session.</p>
          </div>
        )}

        {/* Add New Form Section */}
        {isAdding ? (
          <div className="border border-border rounded-lg p-4 space-y-4 bg-muted/20">
            <h4 className="font-medium text-sm">Add New Form</h4>

            <RadioGroup
              value={newFormType}
              onValueChange={(v) => setNewFormType(v as 'pdf' | 'external_link')}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="pdf" id="type-pdf" />
                <Label htmlFor="type-pdf" className="text-sm">Upload PDF</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="external_link" id="type-link" />
                <Label htmlFor="type-link" className="text-sm">External Link (Google Forms, Typeform, etc.)</Label>
              </div>
            </RadioGroup>

            <div>
              <Label htmlFor="form-title" className="text-sm">Title *</Label>
              <Input
                id="form-title"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="e.g., Client Intake Questionnaire"
                className="mt-1"
                maxLength={100}
              />
            </div>

            <div>
              <Label htmlFor="form-desc" className="text-sm">Description (optional)</Label>
              <Textarea
                id="form-desc"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Brief instructions for the client..."
                className="mt-1"
                rows={2}
                maxLength={500}
              />
            </div>

            {newFormType === 'pdf' ? (
              <div>
                <Label className="text-sm">PDF File *</Label>
                <div className="mt-1">
                  <UnifiedFileUpload
                    accept=".pdf,application/pdf"
                    multiple={false}
                    maxFiles={1}
                    maxSize={10}
                    onFilesChange={(files) => setPendingFile(files[0] || null)}
                    disabled={isSaving}
                    formatHint="PDF files only, max 10MB"
                  >
                    {pendingFile ? pendingFile.name : 'Click to select PDF file'}
                  </UnifiedFileUpload>
                </div>
              </div>
            ) : (
              <div>
                <Label htmlFor="form-url" className="text-sm">External URL *</Label>
                <Input
                  id="form-url"
                  type="url"
                  value={newExternalUrl}
                  onChange={(e) => setNewExternalUrl(e.target.value)}
                  placeholder="https://forms.google.com/..."
                  className="mt-1"
                  maxLength={500}
                />
              </div>
            )}

            <div className="flex gap-2">
              <Button onClick={handleSaveForm} disabled={isSaving} size="sm">
                {isSaving ? 'Saving...' : 'Save Form'}
              </Button>
              <Button variant="outline" onClick={resetNewForm} disabled={isSaving} size="sm">
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          forms.length < 5 && (
            <Button
              variant="outline"
              onClick={() => setIsAdding(true)}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Intake Form
            </Button>
          )
        )}
      </CardContent>
    </Card>
  );
}
